<template>
  <div class="min-h-screen bg-white overflow-hidden">
    <!-- Hero Section with Split Design -->
    <div class="relative">
      <!-- Background Elements -->
      <div class="absolute inset-0 overflow-hidden">
        <div
          class="absolute top-0 right-0 w-1/2 h-full bg-logo-color hidden lg:block"
        ></div>
        <!-- Decorative circles -->
        <div
          class="absolute -top-20 -left-20 w-80 h-80 bg-logo-color/5 rounded-full"
        ></div>
        <div
          class="absolute top-1/2 left-1/3 w-40 h-40 bg-logo-color/10 rounded-full hidden lg:block"
        ></div>
        <div
          class="absolute bottom-20 right-1/4 w-60 h-60 bg-white/10 rounded-full hidden lg:block"
        ></div>
      </div>

      <div class="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div class="grid lg:grid-cols-2 min-h-[80vh] items-center gap-12 py-20">
          <!-- Left Content -->
          <div class="order-2 lg:order-1" data-aos="fade-right">
            <!-- Badge -->
            <div class="inline-flex items-center gap-2 mb-6">
              <div class="w-10 h-1 bg-logo-color rounded-full"></div>
              <span
                class="text-sm uppercase tracking-wider text-logo-color font-semibold"
              >
                Contact Us
              </span>
            </div>

            <h1
              class="text-4xl md:text-5xl lg:text-6xl font-bold text-logo-color mb-6 leading-tight"
            >
              Let's Start a
              <span class="relative inline-block">
                Conversation
                <svg
                  class="absolute -bottom-2 left-0 w-full h-3 text-logo-color/30"
                  viewBox="0 0 200 10"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M0,5 Q50,0 100,5 T200,5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="3"
                  />
                </svg>
              </span>
            </h1>

            <p class="text-lg text-gray-600 mb-10 leading-relaxed max-w-lg">
              Have questions about our programs? Want to volunteer or partner
              with us? We'd love to hear from you. Reach out and let's make a
              difference together.
            </p>

            <!-- Social Links -->
            <div class="flex gap-3">
              <a
                href="https://web.facebook.com/youthallyorganisation"
                target="_blank"
                rel="noopener noreferrer"
                class="w-12 h-12 bg-logo-color/10 rounded-xl flex items-center justify-center hover:bg-logo-color group transition-colors duration-300"
              >
                <i
                  class="fab fa-facebook-f text-logo-color group-hover:text-white transition-colors"
                ></i>
              </a>
              <a
                href="https://www.linkedin.com/in/youth-ally-youth"
                target="_blank"
                rel="noopener noreferrer"
                class="w-12 h-12 bg-logo-color/10 rounded-xl flex items-center justify-center hover:bg-logo-color group transition-colors duration-300"
              >
                <i
                  class="fab fa-linkedin-in text-logo-color group-hover:text-white transition-colors"
                ></i>
              </a>
              <a
                href="https://wa.me/+233249372583"
                target="_blank"
                rel="noopener noreferrer"
                class="w-12 h-12 bg-logo-color/10 rounded-xl flex items-center justify-center hover:bg-green-500 group transition-colors duration-300"
              >
                <i
                  class="fab fa-whatsapp text-logo-color group-hover:text-white transition-colors"
                ></i>
              </a>
            </div>
          </div>

          <!-- Right Content - Card Stack -->
          <div
            class="order-1 lg:order-2 relative"
            data-aos="fade-left"
            data-aos-delay="200"
          >
            <!-- Main Contact Card -->
            <div
              class="bg-white rounded-3xl shadow-2xl p-8 md:p-10 relative z-10 lg:ml-8"
            >
              <!-- Card decorative element -->
              <div
                class="absolute -top-4 -right-4 w-24 h-24 bg-logo-color/10 rounded-full"
              ></div>
              <div
                class="absolute -bottom-6 -left-6 w-32 h-32 bg-logo-color/5 rounded-full"
              ></div>

              <div class="relative">
                <h2 class="text-2xl font-bold text-logo-color mb-2">
                  Get in Touch
                </h2>
                <p class="text-gray-500 mb-8">
                  Choose how you'd like to reach us
                </p>

                <!-- Contact Options -->
                <div class="space-y-4">
                  <!-- WhatsApp -->
                  <a
                    href="https://wa.me/+233249372583"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="group flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-100 hover:border-green-500 hover:bg-green-50 transition-all duration-300"
                  >
                    <div
                      class="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-500 transition-colors duration-300"
                    >
                      <i
                        class="fab fa-whatsapp text-2xl text-green-600 group-hover:text-white transition-colors"
                      ></i>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-bold text-gray-800">WhatsApp</h3>
                      <p class="text-sm text-gray-500">
                        Quick response guaranteed
                      </p>
                    </div>
                    <i
                      class="fas fa-arrow-right text-gray-400 group-hover:text-green-500 group-hover:translate-x-1 transition-all"
                    ></i>
                  </a>

                  <!-- Email -->
                  <a
                    href="mailto:support@youthallynow.org"
                    class="group flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-100 hover:border-logo-color hover:bg-logo-color/5 transition-all duration-300"
                  >
                    <div
                      class="w-14 h-14 bg-logo-color/10 rounded-xl flex items-center justify-center group-hover:bg-logo-color transition-colors duration-300"
                    >
                      <i
                        class="fas fa-envelope text-2xl text-logo-color group-hover:text-white transition-colors"
                      ></i>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-bold text-gray-800">Email</h3>
                      <p class="text-sm text-gray-500">
                        We reply within 24 hours
                      </p>
                    </div>
                    <i
                      class="fas fa-arrow-right text-gray-400 group-hover:text-logo-color group-hover:translate-x-1 transition-all"
                    ></i>
                  </a>

                  <!-- Phone -->
                  <a
                    href="tel:+233249372583"
                    class="group flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-100 hover:border-logo-color hover:bg-logo-color/5 transition-all duration-300"
                  >
                    <div
                      class="w-14 h-14 bg-logo-color/10 rounded-xl flex items-center justify-center group-hover:bg-logo-color transition-colors duration-300"
                    >
                      <i
                        class="fas fa-phone-alt text-2xl text-logo-color group-hover:text-white transition-colors"
                      ></i>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-bold text-gray-800">Phone</h3>
                      <p class="text-sm text-gray-500">Mon-Fri, 8am to 6pm</p>
                    </div>
                    <i
                      class="fas fa-arrow-right text-gray-400 group-hover:text-logo-color group-hover:translate-x-1 transition-all"
                    ></i>
                  </a>
                </div>
              </div>
            </div>

            <!-- Floating elements for visual interest -->
            <div
              class="hidden lg:block absolute -top-8 -right-8 w-20 h-20 bg-logo-color rounded-2xl rotate-12 opacity-80"
            ></div>
            <div
              class="hidden lg:block absolute -bottom-4 right-20 w-12 h-12 bg-logo-color/60 rounded-xl -rotate-12"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Location Section -->
    <div class="bg-gray-50 py-20">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="max-w-6xl mx-auto">
          <div class="grid lg:grid-cols-2 gap-12 items-center">
            <!-- Map/Image Side -->
            <div class="relative" data-aos="fade-right">
              <div
                class="absolute -top-4 -left-4 w-full h-full bg-logo-color/20 rounded-3xl"
              ></div>
              <div
                class="relative bg-white rounded-3xl overflow-hidden shadow-xl"
              >
                <img
                  :src="require('@/assets/contactus.jpg')"
                  alt="Youth Ally Office"
                  class="w-full h-80 object-cover"
                />
                <div class="p-6">
                  <div class="flex items-start gap-4">
                    <div
                      class="w-12 h-12 bg-logo-color rounded-xl flex items-center justify-center flex-shrink-0"
                    >
                      <i class="fas fa-map-marker-alt text-white"></i>
                    </div>
                    <div>
                      <h3 class="font-bold text-logo-color text-lg mb-1">
                        Our Office
                      </h3>
                      <p class="text-gray-600">
                        2nd floor, Sika Plaza<br />
                        Christian Village – Dome Pillar 2 road<br />
                        Kisseman - Accra, Ghana
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Office Hours Side -->
            <div data-aos="fade-left" data-aos-delay="200">
              <div class="inline-flex items-center gap-2 mb-6">
                <div class="w-10 h-1 bg-logo-color rounded-full"></div>
                <span
                  class="text-sm uppercase tracking-wider text-logo-color font-semibold"
                >
                  Visit Us
                </span>
              </div>

              <h2
                class="text-3xl md:text-4xl font-bold text-logo-color mb-6 leading-tight"
              >
                Come Say Hello
              </h2>

              <p class="text-gray-600 mb-8 leading-relaxed">
                We'd love to meet you in person! Drop by our office during
                business hours or schedule an appointment to discuss how we can
                work together.
              </p>

              <!-- Office Hours -->
              <div class="bg-white rounded-2xl p-6 shadow-lg mb-6">
                <h3
                  class="font-bold text-logo-color mb-4 flex items-center gap-2"
                >
                  <i class="fas fa-clock"></i>
                  Office Hours
                </h3>
                <div class="space-y-3">
                  <div class="flex justify-between items-center">
                    <span class="text-gray-600">Monday - Friday</span>
                    <span class="font-semibold text-logo-color"
                      >8:00 AM - 6:00 PM</span
                    >
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-gray-600">Saturday</span>
                    <span class="font-semibold text-logo-color"
                      >By Appointment</span
                    >
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-gray-600">Sunday</span>
                    <span class="font-semibold text-gray-400">Closed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Contact Form Section -->
  <ask-as />
</template>

<script>
import AskAs from "../AskAs.vue";

export default {
  components: { AskAs },
  name: "ContactPage",
};
</script>

<style scoped></style>
